import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { API_ENDPOINTS } from '../constants/api';
import { getSignedUrls } from '../utils/fileOperations';
import { reportError, reportWarning } from '../sentry';

class CreatorService {
  /**
   * Fetch creator data by username
   * @param {string} username - Creator username
   * @returns {Promise<Object>} Creator data response
   */
  async fetchCreator(username) {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.CREATOR}/${username}`);
      
      // Process profile picture with signed URL if it exists
      if (response.data?.data?.user_data?.profile_picture) {
        const profilePictureKey = response.data.data.user_data.profile_picture;
        
        // Check if it's already a full URL (signed URL) or a file key
        if (profilePictureKey && typeof profilePictureKey === 'string' && !profilePictureKey.startsWith('http')) {
          try {
            // Get signed URL for the profile picture
            const signedUrlsData = await getSignedUrls([profilePictureKey], true);
            if (signedUrlsData && signedUrlsData.length > 0) {
              // Replace the file key with signed URL
              response.data.data.user_data.profile_picture = signedUrlsData[0].signedUrl;
            }
          } catch (error) {
            reportError(error, 'signed URL profile picture');
            // Keep the original value if signed URL fetch fails
          }
        }
      }

      // Process profile banner with signed URL if it exists
      if (response.data?.data?.user_data?.profile_banner) {
        const profileBannerKey = response.data.data.user_data.profile_banner;
        
        // Check if it's already a full URL (signed URL) or a file key
        if (profileBannerKey && typeof profileBannerKey === 'string' && !profileBannerKey.startsWith('http')) {
          try {
            // Get signed URL for the profile banner
            const signedUrlsData = await getSignedUrls([profileBannerKey], true);
            if (signedUrlsData && signedUrlsData.length > 0) {
              // Replace the file key with signed URL
              response.data.data.user_data.profile_banner = signedUrlsData[0].signedUrl;
            }
          } catch (error) {
            reportError(error, 'signed URL profile banner');
            // Keep the original value if signed URL fetch fails
          }
        }
      }

      return ResponseHandler.handleSuccess(response, 'Creator data fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch creator data');
    }
  }

  /**
   * Update creator profile
   * @param {Object} profileData - Creator profile data to update
   * @returns {Promise<Object>} Update creator response
   */
  async updateCreator(profileData) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.CREATOR, profileData);
      return ResponseHandler.handleSuccess(response, 'Creator profile updated successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to update creator profile');
    }
  }

  /**
   * Delete creator account
   * @param {string} email - Creator email
   * @returns {Promise<Object>} Delete creator response
   */
  async deleteCreator(email) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.CREATOR, {
        data: { email }
      });
      
      // Clear localStorage after successful deletion
      localStorage.clear();
      
      return ResponseHandler.handleSuccess(response, 'Creator account deleted successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to delete creator account');
    }
  }

  /**
   * Update creator subscription data
   * @param {Object} subscriptionData - Subscription data object with price, allow_tips, promotional_campaign
   * @returns {Promise<Object>} Update subscription response
   */
  async updateSubscriptionData(subscriptionData) {
    try {
      const requestBody = {};
      
      // Only include fields that are provided
      if (subscriptionData.price !== undefined && subscriptionData.price !== '') {
        requestBody.price = subscriptionData.price;
      }
      if (subscriptionData.allow_tips !== undefined) {
        requestBody.allow_tips = subscriptionData.allow_tips;
      }
      if (subscriptionData.promotional_campaign !== undefined) {
        requestBody.promotional_campaign = subscriptionData.promotional_campaign;
      }
      
      const response = await apiClient.put(API_ENDPOINTS.CREATOR_SUBSCRIPTION, requestBody);
      return ResponseHandler.handleSuccess(response, 'Subscription data updated successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to update subscription data');
    }
  }

  /**
   * Post content (single file or bundle)
   * @param {Object} requestBody - Content data to post
   * @returns {Promise<Object>} Post content response
   */
  async postContent(requestBody) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.CREATOR_CONTENT, requestBody);
      return ResponseHandler.handleSuccess(response, 'Content posted successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to post content');
    }
  }

  /**
   * Fetch total tips for a creator
   * @returns {Promise<Object>} Total tips response
   */
  async fetchTotalTip() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.CREATOR_TOTAL_TIP);
      return ResponseHandler.handleSuccess(response, 'Total tips fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch total tips');
    }
  }

  /**
   * Check whether the creator meets the audience threshold for mass messaging.
   * GET /content-creators/audience-threshold
   * @returns {Promise<Object>} ResponseHandler-shaped result
   */
  async getAudienceThreshold() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.AUDIENCE_THRESHOLD);
      return ResponseHandler.handleSuccess(response, 'Creator audience threshold checked');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch audience threshold');
    }
  }

  /**
   * Fetch featured creators for authenticated users
   * @param {Object} params - Query parameters object
   * @param {number} params.limit - Number of featured creators to fetch
   * @param {string} params.sortBy - Optional sort parameter
   * @param {number} params.skip - Optional skip parameter for pagination
   * @returns {Promise<Object>} Featured creators response with signed URLs ({ success, data?, message? })
   */
  async getCreatorLists(params = {}) {
    try {
      // Validate input parameters
      if (params !== null && typeof params !== 'object') {
        return ResponseHandler.handleClientValidationError('Parameters must be an object');
      }

      // Validate and sanitize limit parameter
      if (params.limit !== undefined) {
        const limit = Number(params.limit);
        if (!Number.isInteger(limit) || limit < 1 || limit > 100) {
          return ResponseHandler.handleClientValidationError(
            'Limit must be an integer between 1 and 100',
          );
        }
        params.limit = limit;
      }

      // Validate skip parameter
      if (params.skip !== undefined) {
        const skip = Number(params.skip);
        if (!Number.isInteger(skip) || skip < 0) {
          return ResponseHandler.handleClientValidationError(
            'Skip must be a non-negative integer',
          );
        }
        params.skip = skip;
      }

      // Set default parameters
      const queryParams = { limit: 9, skip: 0, ...params };
      
      // Build query string from params object
      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');
      
      const url = queryString 
        ? `${API_ENDPOINTS.CONTENT_CREATOR}?${queryString}`
        : API_ENDPOINTS.CONTENT_CREATOR;
      
      // Validate API endpoint exists
      if (!API_ENDPOINTS.CONTENT_CREATOR) {
        throw new Error('CONTENT_CREATORS endpoint is not configured');
      }

      const response = await apiClient.get(url);
      
      // Validate response structure
      if (!response || !response.data) {
        throw new Error('Invalid response structure from API');
      }

      // Initialize default data structure
      let processedCreators = [];
      
      // Process creators with signed URLs for profile pictures and banners
      if (response.data?.data && Array.isArray(response.data.data)) {
        try {
          processedCreators = await Promise.all(
            response.data.data.map(async (creator) => {
              // Validate creator object
              if (!creator || typeof creator !== 'object') {
                reportWarning(null, 'Invalid creator object encountered, skipping', {
                  tags: { context: 'getCreatorLists' },
                });
                return null;
              }

              const processedCreator = { ...creator };
              
              // Process profile_picture
              if (creator.profile_picture && 
                  typeof creator.profile_picture === 'string' && 
                  creator.profile_picture.trim() !== '' &&
                  !creator.profile_picture.startsWith('http')) {
                try {
                  const signedUrlsData = await getSignedUrls([creator.profile_picture], true);
                  if (signedUrlsData && Array.isArray(signedUrlsData) && signedUrlsData.length > 0 && signedUrlsData[0]?.signedUrl) {
                    processedCreator.profile_picture = signedUrlsData[0].signedUrl;
                  }
                } catch (error) {
                  reportError(error, 'creator profile picture signed URL', { extra: { creatorId: creator.id || 'unknown' } });
                  // Keep the original value if signed URL fetch fails
                }
              }
              
              // Process profile_banner
              if (creator.profile_banner && 
                  typeof creator.profile_banner === 'string' && 
                  creator.profile_banner.trim() !== '' &&
                  !creator.profile_banner.startsWith('http')) {
                try {
                  const signedUrlsData = await getSignedUrls([creator.profile_banner], true);
                  if (signedUrlsData && Array.isArray(signedUrlsData) && signedUrlsData.length > 0 && signedUrlsData[0]?.signedUrl) {
                    processedCreator.profile_banner = signedUrlsData[0].signedUrl;
                  }
                } catch (error) {
                  reportError(error, 'creator profile banner signed URL', { extra: { creatorId: creator.id || 'unknown' } });
                  // Keep the original value if signed URL fetch fails
                }
              }
              
              return processedCreator;
            })
          );

          // Filter out any null entries from invalid creators
          processedCreators = processedCreators.filter(creator => creator !== null);
        } catch (error) {
          reportError(error, 'processing creators with signed URLs');
          // Fall back to original data if processing fails
          processedCreators = response.data.data || [];
        }
      } else if (response.data?.data && !Array.isArray(response.data.data)) {
        reportWarning(typeof response.data.data, 'Expected data to be an array', {
          tags: { context: 'getCreatorLists' },
        });
      }
      
      // Build response with safe property access and defaults
      return {
        success: true,
        status: response.status || 200,
        message: 'Featured creators fetched successfully',
        data: {
          data: processedCreators,
          recordsFiltered: response.data?.recordsFiltered ?? processedCreators.length,
          recordsTotal: response.data?.recordsTotal ?? processedCreators.length,
          currentPage: response.data?.currentPage ?? 1,
          perPage: response.data?.perPage ?? queryParams.limit,
          totalPages: response.data?.totalPages ?? Math.ceil((response.data?.recordsTotal ?? processedCreators.length) / queryParams.limit),
          sortBy: response.data?.sortBy ?? queryParams.sortBy
        }
      };
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch featured creators');
    }
  }

  /**
   * Fetch featured creators for guest users
   * @param {Object} params - Query parameters object
   * @param {number} params.limit - Number of featured creators to fetch
   * @param {string} params.sortBy - Optional sort parameter
   * @param {number} params.skip - Optional skip parameter for pagination
   * @returns {Promise<Object>} Featured creators response with signed URLs ({ success, data?, message? })
   */
  async getCreatorGuestLists(params = {}) {
    try {
      // Validate input parameters
      if (params !== null && typeof params !== 'object') {
        return ResponseHandler.handleClientValidationError('Parameters must be an object');
      }

      // Validate and sanitize limit parameter
      if (params.limit !== undefined) {
        const limit = Number(params.limit);
        if (!Number.isInteger(limit) || limit < 1 || limit > 100) {
          return ResponseHandler.handleClientValidationError(
            'Limit must be an integer between 1 and 100',
          );
        }
        params.limit = limit;
      }

      // Validate skip parameter
      if (params.skip !== undefined) {
        const skip = Number(params.skip);
        if (!Number.isInteger(skip) || skip < 0) {
          return ResponseHandler.handleClientValidationError(
            'Skip must be a non-negative integer',
          );
        }
        params.skip = skip;
      }

      // Set default parameters
      const queryParams = { 
        limit: 9, 
        sortBy: 'username_asc', 
        ...params 
      };
      
      // Build query string from params object
      const queryString = Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
        .join('&');
      
      const url = queryString 
        ? `${API_ENDPOINTS.CONTENT_CREATOR_GUEST}?${queryString}`
        : API_ENDPOINTS.CONTENT_CREATOR_GUEST;
      
      // Validate API endpoint exists
      if (!API_ENDPOINTS.CONTENT_CREATOR_GUEST) {
        throw new Error('CONTENT_CREATOR_GUEST endpoint is not configured');
      }

      const response = await apiClient.get(url);
      
      // Validate response structure
      if (!response || !response.data) {
        throw new Error('Invalid response structure from API');
      }

      // Initialize default data structure
      let processedCreators = [];
      
      // Process creators with signed URLs for profile pictures and banners
      if (response.data?.data && Array.isArray(response.data.data)) {
        try {
          processedCreators = await Promise.all(
            response.data.data.map(async (creator) => {
              // Validate creator object
              if (!creator || typeof creator !== 'object') {
                reportWarning(null, 'Invalid creator object encountered, skipping', {
                  tags: { context: 'getCreatorGuestLists' },
                });
                return null;
              }

              const processedCreator = { ...creator };
              
              // Process profile_picture
              if (creator.profile_picture && 
                  typeof creator.profile_picture === 'string' && 
                  creator.profile_picture.trim() !== '' &&
                  !creator.profile_picture.startsWith('http')) {
                try {
                  const signedUrlsData = await getSignedUrls([creator.profile_picture], true);
                  if (signedUrlsData && Array.isArray(signedUrlsData) && signedUrlsData.length > 0 && signedUrlsData[0]?.signedUrl) {
                    processedCreator.profile_picture = signedUrlsData[0].signedUrl;
                  }
                } catch (error) {
                  reportError(error, 'creator profile picture signed URL', { extra: { creatorId: creator.id || 'unknown' } });
                  // Keep the original value if signed URL fetch fails
                }
              }
              
              // Process profile_banner
              if (creator.profile_banner && 
                  typeof creator.profile_banner === 'string' && 
                  creator.profile_banner.trim() !== '' &&
                  !creator.profile_banner.startsWith('http')) {
                try {
                  const signedUrlsData = await getSignedUrls([creator.profile_banner], true);
                  if (signedUrlsData && Array.isArray(signedUrlsData) && signedUrlsData.length > 0 && signedUrlsData[0]?.signedUrl) {
                    processedCreator.profile_banner = signedUrlsData[0].signedUrl;
                  }
                } catch (error) {
                  reportError(error, 'creator profile banner signed URL', { extra: { creatorId: creator.id || 'unknown' } });
                  // Keep the original value if signed URL fetch fails
                }
              }
              
              return processedCreator;
            })
          );

          // Filter out any null entries from invalid creators
          processedCreators = processedCreators.filter(creator => creator !== null);
        } catch (error) {
          reportError(error, 'processing creators with signed URLs');
          // Fall back to original data if processing fails
          processedCreators = response.data.data || [];
        }
      } else if (response.data?.data && !Array.isArray(response.data.data)) {
        reportWarning(typeof response.data.data, 'Expected data to be an array', {
          tags: { context: 'getCreatorGuestLists' },
        });
      }
      
      // Build response with safe property access and defaults
      return {
        success: true,
        status: response.status || 200,
        message: 'Featured creators fetched successfully',
        data: {
          data: processedCreators,
          recordsFiltered: response.data?.recordsFiltered ?? processedCreators.length,
          recordsTotal: response.data?.recordsTotal ?? processedCreators.length,
          currentPage: response.data?.currentPage ?? 1,
          perPage: response.data?.perPage ?? queryParams.limit,
          totalPages: response.data?.totalPages ?? Math.ceil((response.data?.recordsTotal ?? processedCreators.length) / queryParams.limit),
          sortBy: response.data?.sortBy ?? queryParams.sortBy
        }
      };
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch featured creators for guest users');
    }
  }
}

export default new CreatorService();
